<script setup>
/**
 * Fluxo de criação de conexão WhatsApp, espelhando o assistente do Aceleres
 * (/integracoes): nome → tipo/API → aviso de versão → pareamento.
 *
 * O hub usava um formulário inline com selects crus: o gestor escolhia
 * "Individual da empresa" ou "API 1" sem nenhuma explicação do que cada opção
 * significa, e nada avisava que um WhatsApp desatualizado derruba o
 * pareamento. As três etapas abaixo existem para resolver exatamente isso.
 */
import { computed, nextTick, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  sellers: {
    type: Array,
    default: () => [],
  },
  providers: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  /**
   * Estado do pareamento, mandado pelo hub enquanto a etapa final está aberta:
   * `{ base64, pairingCode, pairingStarted, status }`.
   *
   * O QR morava numa <section> solta no fim da página de integrações, abaixo da
   * lista de conexões: quem terminava o assistente via o modal fechar e tinha de
   * rolar a tela atrás do código. Ele é uma etapa do fluxo e agora mora aqui.
   */
  pairing: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['create', 'close', 'refreshQr', 'cancelPairing']);

const dialogRef = ref(null);

const STEPS = [
  { key: 'name', label: 'Nome' },
  { key: 'type', label: 'Tipo e API' },
  { key: 'warning', label: 'Antes de conectar' },
  { key: 'pairing', label: 'Pareamento' },
];

// Espelha os cartões do Aceleres. `shared` não aparece na criação lá e também
// não aparece aqui — continua disponível na edição para conexões antigas.
const CONNECTION_TYPES = [
  {
    value: 'individual_own',
    title: 'Individual Próprio',
    icon: 'i-lucide-lock',
    iconClass: 'bg-n-blue-3 text-n-blue-11',
    description:
      'WhatsApp pessoal do usuário. Somente o proprietário acessa as conversas — o gestor não vê nada por padrão. O gestor só visualiza as conversas que o próprio usuário definir como atendimento da empresa.',
  },
  {
    value: 'individual_company',
    title: 'Individual da Empresa',
    icon: 'i-lucide-building-2',
    iconClass: 'bg-n-amber-3 text-n-amber-11',
    description:
      'Número oficial de propriedade da empresa, com acompanhamento do gestor e responsável definido.',
  },
  {
    value: 'shared',
    title: 'Compartilhado',
    icon: 'i-lucide-users',
    iconClass: 'bg-n-teal-3 text-n-teal-11',
    description:
      'Número disponível para a equipe, com atendimento compartilhado e rodízio automático.',
  },
];

// Fallback para o caso de o bridge do Aceleres não informar os providers.
// A regra é a mesma de lá: a Evolution só reaparece se a WAME não existir.
const FALLBACK_PROVIDERS = [
  {
    key: 'api2',
    label: 'Conexão Zoraxy',
    description: 'Conexão principal.',
  },
  // Z-API fica de fora: é só para administrador e vem da ponte.
];

// As três instruções do aparelho, na ordem em que o WhatsApp as apresenta.
const PAIRING_HINTS = [
  { icon: 'i-lucide-smartphone', text: 'Abra o', strong: 'WhatsApp' },
  { icon: 'i-lucide-link-2', text: 'Toque em', strong: 'Aparelhos conectados' },
  { icon: 'i-lucide-scan-line', text: 'Escaneie', strong: 'este código QR' },
];

const stepIndex = ref(0);
// 'create' percorre o assistente inteiro; 'pairing' abre direto no QR, para
// reconectar uma inbox que já existe — repetir nome, tipo e API seria pedir de
// novo o que já está decidido.
const mode = ref('create');
const displayName = ref('');
const connectionType = ref('individual_own');
const assigneeUserId = ref('');
const provider = ref('');
const error = ref('');

const currentStep = computed(() => STEPS[stepIndex.value].key);

// No modo pareamento não há etapas anteriores a percorrer: mostrar um stepper de
// quatro passos com três já vencidos anunciaria um caminho que a pessoa não fez.
const visibleSteps = computed(() => (mode.value === 'create' ? STEPS : []));

const isPairing = computed(() => currentStep.value === 'pairing');

// Enquanto o hub não devolve imagem nem código, a etapa mostra o carregador: o
// pareamento começa antes de o provedor ter o QR pronto.
const hasPairingCode = computed(
  () => !!(props.pairing?.base64 || props.pairing?.pairingCode)
);

const availableProviders = computed(() =>
  props.providers.length ? props.providers : FALLBACK_PROVIDERS
);

const requiresAssignee = computed(
  () => connectionType.value === 'individual_company'
);

const sellerOptions = computed(() => props.sellers || []);

const reset = () => {
  stepIndex.value = 0;
  mode.value = 'create';
  displayName.value = '';
  connectionType.value = 'individual_own';
  assigneeUserId.value = '';
  // Um único provider disponível não é uma escolha: já vem marcado.
  provider.value =
    availableProviders.value.length === 1
      ? availableProviders.value[0].key
      : '';
  error.value = '';
};

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

/**
 * Avança para o pareamento depois que a inbox foi criada.
 *
 * O assistente fechava aqui e o QR aparecia solto no fim da página; agora a
 * criação apenas emenda a última etapa, sem trocar de superfície.
 */
const beginPairing = () => {
  error.value = '';
  stepIndex.value = STEPS.findIndex(step => step.key === 'pairing');
};

/**
 * Abre já no QR, para uma inbox que existe — conectada antes ou nunca pareada.
 */
const openForPairing = ({ name } = {}) => {
  reset();
  mode.value = 'pairing';
  displayName.value = name || '';
  stepIndex.value = STEPS.findIndex(step => step.key === 'pairing');
  dialogRef.value?.open();
};

defineExpose({ open, close, beginPairing, openForPairing });

watch(
  () => connectionType.value,
  () => {
    error.value = '';
  }
);

/**
 * No celular o corpo do assistente rola, e a mensagem de erro nasce no fim
 * dele — fora da area visivel, atras do rodape fixo. Quem clicava "Continuar"
 * sem escolher a API via o botao nao fazer absolutamente nada. Trazer o erro
 * para a vista e o que transforma isso de volta em recusa legivel.
 *
 * No desktop o corpo nao rola e a chamada nao tem efeito nenhum.
 */
const errorRef = ref(null);

watch(error, async value => {
  if (!value) return;
  await nextTick();
  errorRef.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
});

const goBack = () => {
  error.value = '';
  // Sair do pareamento é abandonar uma tentativa em curso, não voltar uma tela:
  // quem decide o que fazer com a sessão remota é o hub.
  if (isPairing.value) {
    emit('cancelPairing');
    close();
    return;
  }
  if (stepIndex.value === 0) {
    close();
    return;
  }
  stepIndex.value -= 1;
};

const submit = () => {
  emit('create', {
    display_name: displayName.value.trim(),
    connection_type: connectionType.value,
    default_assignee_user_id: requiresAssignee.value
      ? Number(assigneeUserId.value)
      : null,
    connection_api: provider.value,
  });
};

const advance = () => {
  if (isPairing.value) {
    emit('refreshQr');
    return;
  }

  if (currentStep.value === 'name') {
    if (!displayName.value.trim()) {
      error.value = 'Informe um nome para a inbox.';
      return;
    }
    error.value = '';
    stepIndex.value = 1;
    return;
  }

  if (currentStep.value === 'type') {
    if (!provider.value) {
      error.value = 'Escolha uma API para continuar.';
      return;
    }
    if (requiresAssignee.value && !assigneeUserId.value) {
      error.value =
        'Escolha o vendedor responsável pelo número corporativo individual.';
      return;
    }
    error.value = '';
    stepIndex.value = 2;
    return;
  }

  submit();
};

const primaryLabel = computed(() => {
  if (currentStep.value === 'name') return 'Continuar';
  if (currentStep.value === 'type') return 'Continuar';
  if (isPairing.value) return 'Atualizar código';
  return 'Entendi, continuar';
});

const secondaryLabel = computed(() => {
  if (isPairing.value) return 'Cancelar tentativa';
  return stepIndex.value === 0 ? 'Cancelar' : 'Voltar';
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Dialog
    ref="dialogRef"
    class="wa-wizard-dialog max-md:!my-4 max-md:!max-h-[calc(100dvh_-_2rem)] max-md:!overflow-hidden"
    width="xl"
    position="top"
    overflow-y-auto
    show-close-button
    mobile-inset
    panel-class="max-md:max-h-[calc(100dvh_-_2rem)] max-md:gap-3 max-md:overflow-hidden max-md:p-4"
    @close="emit('close')"
  >
    <div
      class="wa-wizard flex flex-col gap-5 max-md:min-h-0 max-md:flex-1 max-md:gap-4 max-md:overflow-y-auto max-md:overflow-x-hidden max-md:overscroll-contain max-md:scrollbar-thin"
    >
      <!-- Stepper: sem ele o usuário não sabe quantas telas faltam. -->
      <ol
        v-if="visibleSteps.length"
        class="wa-wizard__steps flex items-center gap-2 max-md:ltr:pr-8 max-md:rtl:pl-8"
      >
        <li
          v-for="(step, index) in visibleSteps"
          :key="step.key"
          class="wa-wizard__step flex flex-1 items-center gap-2 max-md:min-w-0"
        >
          <span
            class="wa-wizard__step-badge flex size-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors"
            :class="
              index <= stepIndex
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            <i
              v-if="index < stepIndex"
              class="wa-wizard__step-check i-lucide-check text-sm"
            />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <!--
            No celular nenhum rotulo cabe aqui: os quatro somados passam da
            largura da tela e, como o `li` nao encolhia, o stepper empurrava o
            modal inteiro para fora do viewport — o conteudo aparecia cortado
            dos dois lados. `truncate` sozinho nao resolve: sobra "Tip…", que
            informa menos que nada. Ficam so as bolinhas numeradas, e o nome da
            etapa atual vai por extenso na linha logo abaixo.
          -->
          <span
            class="wa-wizard__step-label truncate text-xs font-medium max-md:hidden"
            :class="index <= stepIndex ? 'text-n-slate-12' : 'text-n-slate-10'"
          >
            {{ step.label }}
          </span>
          <span
            v-if="index < visibleSteps.length - 1"
            class="wa-wizard__step-line h-px flex-1 bg-n-weak"
            aria-hidden="true"
          />
        </li>
      </ol>

      <!-- So no celular: substitui os rotulos que sairam do stepper acima. -->
      <p
        v-if="visibleSteps.length"
        class="wa-wizard__step-current text-xs font-medium text-n-slate-11 md:hidden"
      >
        Etapa {{ stepIndex + 1 }} de {{ visibleSteps.length }} —
        <span class="font-semibold text-n-slate-12">
          {{ visibleSteps[stepIndex].label }}
        </span>
      </p>

      <!-- Etapa 1 — nome -->
      <div
        v-if="currentStep === 'name'"
        class="wa-wizard__pane wa-wizard__pane--name flex flex-col gap-4"
      >
        <div
          class="wa-wizard__intro flex flex-col items-center gap-3 text-center"
        >
          <div
            class="wa-wizard__intro-icon flex size-12 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-box text-2xl" />
          </div>
          <h3
            class="wa-wizard__intro-title text-lg font-semibold text-n-slate-12"
          >
            Nova inbox de WhatsApp
          </h3>
          <p class="wa-wizard__intro-text text-sm leading-6 text-n-slate-11">
            Uma inbox é a caixa de entrada de um número de WhatsApp: todas as
            conversas recebidas nesse número chegam nela, no painel de
            atendimento, para você e sua equipe responderem. Após dar um nome,
            você escolhe o tipo de conexão e conecta o número escaneando um QR
            Code.
          </p>
        </div>

        <label
          class="wa-wizard__field wa-wizard__field--name flex flex-col gap-1.5"
        >
          <span
            class="wa-wizard__field-label text-sm font-medium text-n-slate-12"
          >
            Nome da inbox
          </span>
          <input
            v-model="displayName"
            class="wa-wizard__name-input rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
            maxlength="60"
            placeholder="Ex.: Vendas, Suporte, WhatsApp Acelerity"
            @keyup.enter="advance"
          />
        </label>

        <p
          class="wa-wizard__hint flex items-start gap-2 rounded-xl bg-n-alpha-1 px-3 py-2.5 text-xs leading-5 text-n-slate-11"
        >
          <i class="i-lucide-info mt-0.5 flex-shrink-0 text-sm" />
          <span>
            <strong class="text-n-slate-12">Dica</strong> — use um nome que
            facilite a identificação da inbox pela sua equipe. Você pode
            renomeá-la depois.
          </span>
        </p>
      </div>

      <!-- Etapa 2 — tipo de conexão e API -->
      <div
        v-else-if="currentStep === 'type'"
        class="wa-wizard__pane wa-wizard__pane--type flex flex-col gap-4"
      >
        <div
          class="wa-wizard__intro flex flex-col items-center gap-2 text-center"
        >
          <div
            class="wa-wizard__intro-icon flex size-12 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-git-branch text-2xl" />
          </div>
          <h3
            class="wa-wizard__intro-title text-lg font-semibold text-n-slate-12"
          >
            Tipo de conexão
          </h3>
          <p class="wa-wizard__intro-text text-sm text-n-slate-11">
            Como este número WhatsApp será usado?
          </p>
        </div>

        <div class="wa-wizard__types flex flex-col gap-2">
          <button
            v-for="type in CONNECTION_TYPES"
            :key="type.value"
            type="button"
            class="wa-wizard__type flex w-full items-start gap-3 rounded-2xl border p-3 text-start transition-colors"
            :class="
              connectionType === type.value
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
            @click="connectionType = type.value"
          >
            <span
              class="wa-wizard__type-icon flex size-9 flex-shrink-0 items-center justify-center rounded-xl"
              :class="type.iconClass"
            >
              <i :class="type.icon" class="text-lg" />
            </span>
            <span class="wa-wizard__type-body min-w-0 flex-1">
              <span
                class="wa-wizard__type-title block text-sm font-semibold text-n-slate-12"
              >
                {{ type.title }}
              </span>
              <span
                class="wa-wizard__type-description mt-1 block text-xs leading-5 text-n-slate-11"
              >
                {{ type.description }}
              </span>
            </span>
            <span
              class="wa-wizard__type-radio mt-1 flex size-4 flex-shrink-0 items-center justify-center rounded-full border"
              :class="
                connectionType === type.value
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-weak'
              "
            >
              <i
                v-if="connectionType === type.value"
                class="i-lucide-check text-[10px] text-white"
              />
            </span>
          </button>
        </div>

        <!-- Fora do cartão de propósito: um <select> dentro de um <button> é
             HTML inválido e o clique abriria o cartão em vez do menu. -->
        <label
          v-if="requiresAssignee"
          class="wa-wizard__field wa-wizard__field--assignee flex flex-col gap-1.5"
        >
          <span
            class="wa-wizard__field-label text-sm font-medium text-n-slate-12"
          >
            Atribuir automaticamente a:
          </span>
          <select
            v-model="assigneeUserId"
            class="wa-wizard__assignee-select rounded-xl border border-n-weak bg-n-background px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">Selecione um vendedor</option>
            <option
              v-for="seller in sellerOptions"
              :key="seller.id"
              :value="seller.id"
            >
              {{ seller.name }}
            </option>
          </select>
          <span
            v-if="!sellerOptions.length"
            class="wa-wizard__assignee-empty text-xs text-n-amber-11"
          >
            Nenhum vendedor disponível para atribuição.
          </span>
        </label>

        <!-- Com uma opção só não há escolha: ela já vem marcada. -->
        <div
          v-if="availableProviders.length > 1"
          class="wa-wizard__providers flex flex-col gap-2"
        >
          <span
            class="wa-wizard__field-label text-sm font-medium text-n-slate-12"
          >
            Selecione a API de conexão
          </span>
          <div class="wa-wizard__provider-list flex flex-wrap gap-2">
            <!--
              A marca de selecionado usa `n-teal` porque **não existe escala
              `n-green` neste design system** — `border-n-green-7 bg-n-green-2`
              não gerava CSS nenhum, e clicar em API2 ou Z-API não mudava nada na
              tela. Confirme antes de trocar de cor:
              `grep -c -- "bg-n-<cor>-2" public/vite/assets/dashboard-*.css`
              — classe usada e não gerada é classe inexistente.

              O anel e o ícone existem porque só a borda era fraca demais para
              indicar escolha num grupo de dois cartões.
            -->
            <button
              v-for="item in availableProviders"
              :key="item.key"
              type="button"
              :aria-pressed="provider === item.key"
              class="wa-wizard__provider flex min-w-[9rem] flex-1 flex-col gap-0.5 rounded-xl border p-3 text-start transition-colors"
              :class="
                provider === item.key
                  ? 'border-n-teal-7 bg-n-teal-2 ring-2 ring-n-teal-7'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
              @click="provider = item.key"
            >
              <span
                class="wa-wizard__provider-title flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
              >
                <i
                  v-if="provider === item.key"
                  class="i-lucide-check-circle-2 text-n-teal-11"
                />
                {{ item.label }}
              </span>
              <span
                v-if="item.description"
                class="wa-wizard__provider-description text-xs leading-5 text-n-slate-11"
              >
                {{ item.description }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Etapa 3 — aviso obrigatório de versão do WhatsApp -->
      <div
        v-else-if="currentStep === 'warning'"
        class="wa-wizard__pane wa-wizard__pane--version flex flex-col gap-4"
      >
        <div
          class="wa-wizard__intro flex flex-col items-center gap-2 text-center"
        >
          <div
            class="wa-wizard__intro-icon flex size-12 items-center justify-center rounded-2xl bg-n-teal-3 text-n-teal-11"
          >
            <i class="i-ri-whatsapp-line text-2xl" />
          </div>
          <h3
            class="wa-wizard__intro-title text-lg font-semibold text-n-slate-12"
          >
            Mantenha o
            <span class="text-n-ruby-11">WhatsApp</span> sempre atualizado
          </h3>
          <p class="wa-wizard__intro-text text-sm text-n-slate-11">
            Para evitar erros de conexão, é <strong>obrigatório</strong>
            utilizar a versão mais recente do WhatsApp.
          </p>
        </div>

        <div class="wa-wizard__notes flex flex-col gap-2">
          <div
            class="wa-wizard__note wa-wizard__note--why flex items-start gap-3 rounded-2xl border border-n-weak p-3"
          >
            <span
              class="wa-wizard__note-icon flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
            >
              <i class="i-lucide-shield-check text-lg" />
            </span>
            <span class="wa-wizard__note-body">
              <span
                class="wa-wizard__note-title block text-sm font-semibold text-n-slate-12"
              >
                Por que isso é importante?
              </span>
              <span
                class="wa-wizard__note-text mt-1 block text-xs leading-5 text-n-slate-11"
              >
                Versões desatualizadas podem causar falhas na leitura do QR
                Code, desconexões e perda de mensagens.
              </span>
            </span>
          </div>
          <div
            class="wa-wizard__note wa-wizard__note--update flex items-start gap-3 rounded-2xl border border-n-weak p-3"
          >
            <span
              class="wa-wizard__note-icon flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-amber-3 text-n-amber-11"
            >
              <i class="i-lucide-refresh-cw text-lg" />
            </span>
            <span class="wa-wizard__note-body">
              <span
                class="wa-wizard__note-title block text-sm font-semibold text-n-slate-12"
              >
                Atualize agora
              </span>
              <span
                class="wa-wizard__note-text mt-1 block text-xs leading-5 text-n-slate-11"
              >
                Abra a loja de apps no seu celular e verifique se há
                atualizações disponíveis do WhatsApp.
              </span>
            </span>
          </div>
        </div>

        <div
          class="wa-wizard__summary rounded-2xl border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-xs leading-5 text-n-slate-11"
        >
          <strong class="text-n-slate-12">{{ displayName }}</strong>
          será criada como
          <strong class="text-n-slate-12">
            {{
              CONNECTION_TYPES.find(item => item.value === connectionType)
                ?.title
            }}
          </strong>
          usando
          <strong class="text-n-slate-12">
            {{
              availableProviders.find(item => item.key === provider)?.label ||
              provider
            }}
          </strong>
          . Em seguida você lê o QR Code para parear o número.
        </div>
      </div>

      <!-- Etapa 4 — pareamento. O QR é uma etapa do fluxo, não um bloco perdido
           no fim da página: quem chega aqui está no meio de uma conexão. -->
      <div
        v-else
        class="wa-wizard__pane wa-wizard__pane--pairing flex flex-col gap-4"
      >
        <div
          class="wa-wizard__intro flex flex-col items-center gap-2 text-center"
        >
          <div
            class="wa-wizard__intro-icon flex size-12 items-center justify-center rounded-2xl bg-n-teal-3 text-n-teal-11"
          >
            <i class="i-ri-whatsapp-line text-2xl" />
          </div>
          <h3
            class="wa-wizard__intro-title text-lg font-semibold text-n-slate-12"
          >
            Conectar
            <span v-if="displayName" class="text-n-ruby-11">
              “{{ displayName }}”
            </span>
          </h3>
          <p class="wa-wizard__intro-text text-sm text-n-slate-11">
            O código é renovado sozinho e o estado da conexão é atualizado
            automaticamente.
          </p>
        </div>

        <ol
          class="wa-wizard__pairing-steps flex items-center gap-2 rounded-2xl border border-n-weak p-3"
        >
          <li
            v-for="(hint, index) in PAIRING_HINTS"
            :key="hint.text"
            class="wa-wizard__pairing-step flex min-w-0 flex-1 items-center gap-2"
          >
            <span
              class="wa-wizard__pairing-step-icon flex size-8 flex-shrink-0 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-11"
            >
              <i :class="hint.icon" class="text-base" />
            </span>
            <span
              class="wa-wizard__pairing-step-label min-w-0 text-xs leading-4 text-n-slate-11"
            >
              {{ index + 1 }}. {{ hint.text }}
              <strong class="block text-n-slate-12">{{ hint.strong }}</strong>
            </span>
          </li>
        </ol>

        <div
          class="wa-wizard__pairing-box flex flex-col items-center gap-3 rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <template v-if="pairing && pairing.pairingStarted">
            <p
              class="wa-wizard__pairing-read text-sm font-semibold text-n-slate-12"
            >
              Código lido. Concluindo a conexão…
            </p>
            <p class="wa-wizard__pairing-hold text-xs text-n-slate-11">
              Não feche esta janela até a confirmação.
            </p>
          </template>
          <template v-else>
            <img
              v-if="pairing && pairing.base64"
              class="wa-wizard__pairing-image size-56 rounded-xl bg-white object-contain p-2"
              :src="
                pairing.base64.startsWith('data:')
                  ? pairing.base64
                  : `data:image/png;base64,${pairing.base64}`
              "
              alt="QR Code para pareamento do WhatsApp"
            />
            <div
              v-else-if="!hasPairingCode"
              class="wa-wizard__pairing-loading flex size-56 items-center justify-center rounded-xl border border-dashed border-n-weak text-xs text-n-slate-10"
            >
              Preparando o código…
            </div>
            <p
              v-if="pairing && pairing.pairingCode"
              class="wa-wizard__pairing-code text-sm text-n-slate-11"
            >
              Ou informe o código de pareamento:
              <strong class="tracking-[0.2em] text-n-slate-12">
                {{ pairing.pairingCode }}
              </strong>
            </p>
          </template>
          <p
            v-if="pairing && pairing.status"
            class="wa-wizard__pairing-status text-xs text-n-slate-10"
          >
            {{ pairing.status }}
          </p>
        </div>
      </div>

      <p
        v-if="error"
        ref="errorRef"
        class="wa-wizard__error flex items-start gap-2 text-xs font-medium text-n-ruby-11"
      >
        <i class="i-lucide-circle-alert mt-0.5 text-sm" />
        {{ error }}
      </p>
    </div>

    <template #footer>
      <div
        class="wa-wizard__footer flex items-center gap-3 max-md:flex-shrink-0 max-md:border-t max-md:border-n-weak max-md:pt-3"
      >
        <Button
          type="button"
          variant="faded"
          color="slate"
          class="wa-wizard__back w-full"
          :label="secondaryLabel"
          @click="goBack"
        />
        <Button
          type="button"
          class="wa-wizard__advance w-full"
          :label="primaryLabel"
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="advance"
        />
      </div>
      <p
        class="wa-wizard__security mt-3 flex items-center justify-center gap-1.5 text-xs text-n-slate-10 max-md:mt-0 max-md:flex-shrink-0"
      >
        <i class="i-lucide-lock text-xs" />
        Seus dados estão protegidos com segurança de ponta.
      </p>
    </template>
  </Dialog>
</template>
